import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../common/Button';
import NameFields from './NameFields';

interface NameModalMobileProps {
  onSubmit: (firstName: string, lastName: string) => void;
  onClose: () => void;
}

const NameModalMobile = ({ onSubmit, onClose }: NameModalMobileProps) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const firstNameRef = useRef<HTMLInputElement>(null);

  const isValid = firstName.trim().length > 0 && lastName.trim().length > 0;

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  // Prevent body scroll and viewport issues on iOS when modal is open
  useEffect(() => {
    const scrollY = window.scrollY;
    const originalBodyStyle = document.body.style.cssText;
    const originalHtmlStyle = document.documentElement.style.cssText;

    // Lock both html and body to prevent iOS Safari viewport issues
    document.documentElement.style.cssText = `
      overflow: hidden;
      height: 100%;
    `;
    document.body.style.cssText = `
      overflow: hidden;
      position: fixed;
      top: -${scrollY}px;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;
    `;

    return () => {
      document.documentElement.style.cssText = originalHtmlStyle;
      document.body.style.cssText = originalBodyStyle;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onSubmit(firstName.trim(), lastName.trim());
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex max-w-full items-end overflow-hidden bg-black/40 backdrop-blur-sm"
      aria-modal="true"
      role="dialog"
      style={{ touchAction: 'none' }}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-full overflow-x-hidden rounded-t-2xl bg-surface-card p-5 pb-8 shadow-xl"
        style={{ touchAction: 'pan-y' }}
      >
        {/* Close button */}
        <div className="mb-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-primary text-foreground-tertiary transition-colors hover:bg-surface-card hover:text-foreground-primary"
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="mb-5 text-center">
          <h2 className="font-heading text-lg font-semibold text-foreground-primary">
            {t('chat.nameModal.title')}
          </h2>
          <p className="mt-1.5 font-body text-[13px] text-foreground-secondary">
            {t('chat.nameModal.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <NameFields
            variant="mobile"
            firstName={firstName}
            lastName={lastName}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            firstNameRef={firstNameRef}
          />

          <Button
            type="submit"
            variant="solid"
            size="sm"
            fullWidth
            disabled={!isValid}
            className="mt-1 rounded-[10px] py-3.5"
          >
            {t('chat.nameModal.continue')}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default NameModalMobile;
